import { useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { REVIEWS } from "@/lib/data";

export default function ReviewsSection() {
    const trackRef = useRef(null);

    const scrollByCard = (dir) => {
        const track = trackRef.current;
        if (!track) return;
        const firstCard = track.querySelector("[data-review-card]");
        const step = firstCard ? firstCard.getBoundingClientRect().width + 24 : 360;
        track.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    return (
        <section data-testid="reviews-section" className="section-pad bg-bone">
            <div className="container-wide">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
                    <div className="max-w-3xl">
                        <div className="overline mb-5">Testimonials</div>
                        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-tight">
                            What our clients say.
                        </h2>
                    </div>

                    <div className="hidden md:flex items-center gap-2 shrink-0">
                        <button
                            onClick={() => scrollByCard(-1)}
                            aria-label="Previous review"
                            data-testid="reviews-prev"
                            className="w-12 h-12 border border-line hover:border-clay flex items-center justify-center text-ink hover:text-clay transition-colors"
                        >
                            <ArrowLeft size={18} strokeWidth={1.5} />
                        </button>
                        <button
                            onClick={() => scrollByCard(1)}
                            aria-label="Next review"
                            data-testid="reviews-next"
                            className="w-12 h-12 border border-line hover:border-clay flex items-center justify-center text-ink hover:text-clay transition-colors"
                        >
                            <ArrowRight size={18} strokeWidth={1.5} />
                        </button>
                    </div>
                </div>

                <div
                    ref={trackRef}
                    data-testid="reviews-track"
                    className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-6 -mx-4 md:-mx-6 px-4 md:px-6 [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: "none" }}
                >
                    {REVIEWS.map((r, i) => (
                        <motion.div
                            key={r.name}
                            data-review-card
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: i * 0.08 }}
                            data-testid={`review-card-${i}`}
                            className="snap-start shrink-0 w-[85%] sm:w-[60%] md:w-[420px] bg-bone2 p-8 md:p-10 border border-line hover:border-clay/40 transition-colors duration-500 flex flex-col"
                        >
                            <Quote size={28} strokeWidth={1.25} className="text-clay mb-6" />

                            <div className="flex gap-1 mb-5">
                                {[...Array(r.rating)].map((_, j) => (
                                    <Star key={`${r.name}-star-${j}`} size={14} strokeWidth={0} fill="#A5634D" />
                                ))}
                            </div>

                            <p className="text-ink2 leading-relaxed text-[15px] mb-8 flex-1">
                                "{r.text}"
                            </p>

                            <div className="flex items-center gap-3 pt-6 border-t border-line">
                                <div className="w-10 h-10 rounded-full bg-clay text-bone flex items-center justify-center font-display text-lg">
                                    {r.name.charAt(0)}
                                </div>
                                <div>
                                    <div className="font-medium text-ink text-sm">{r.name}</div>
                                    <div className="text-xs text-ink2">{r.suburb} · Google Review</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-14 text-center space-y-5">
                    <a
                        href="https://www.google.com/search?q=Brazier+Building+%26+Design+Central+Coast+reviews#lrd=0x0:0x0,1"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="reviews-google-link"
                        className="inline-block text-[11px] tracking-[0.28em] uppercase text-clay hover:text-ink underline underline-offset-8 decoration-clay/40 hover:decoration-ink transition-colors"
                    >
                        Read all reviews on Google
                    </a>
                    <div>
                        <Link to="/contact" data-testid="reviews-cta-contact" className="btn-primary">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
